import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AcreageReportService } from '../../core/services/acreage-report.service';
import { ChemicalReportService } from '../../core/services/chemical-report.service';
import {
  AcreageReportOrgDetail
} from '../../core/models/acreage-report.model';
import {
  ChemicalReportOrgDetail
} from '../../core/models/chemical-report.model';
import { DRONE_RESOURCE_TYPES, DroneResource } from 'src/app/core/models';
import { RemoveDashPipe } from "src/app/shared/pipes/remove-dash.pipe";
import { DroneResourceService } from 'src/app/core/services/drone-resources.service';
import { DroneResourceUploadModalComponent } from 'src/app/shared/components/drone-resource-upload-modal.component';
import { FileUploadService } from 'src/app/core/services/file-upload.service';
import { ConfirmationModalComponent } from 'src/app/shared/components/confirmation-modal.component';

declare var bootstrap: any;

interface DroneInventory {
  id: string;
  name: string;
  manufacturerId: string;
  modelId: string;
  serialNumber: string;
  packagingSerialNumber: string | null;
  version: string | null;
  status: 'available' | 'assigned' | 'maintenance' | 'retired';
  inventoryNote: string | null;
  // Remote ID and broadcast module
  remoteId: string | null;
  remoteBroadcastModule: string | null;
  // FAA N-Number registration
  nNumber: string | null;
  nNumberActiveDate: string | null;
  // Joined fields
  manufacturerName?: string;
  modelName?: string;
  createdAt: string;
  updatedAt: string;
  // Zoho FSM sync fields (for Service Appointments/Assets)
  zohoFsmProductId: string | null;
  zohoFsmSyncStatus: 'pending' | 'synced' | 'error' | null;
  zohoFsmSyncedAt: string | null;
  zohoFsmSyncError: string | null;
  // Zoho Desk sync fields (for Support Tickets)
  zohoDeskProductId: string | null;
  zohoDeskSyncStatus: 'pending' | 'synced' | 'error' | null;
  zohoDeskSyncedAt: string | null;
  zohoDeskSyncError: string | null;
}

interface DroneAssignment {
  id: string;
  droneId: string;
  organizationId: string;
  organizationName?: string;
  assignedToProfileId: string | null;
  assignedByProfileId: string;
  assignedAt: string;
  returnedAt: string | null;
  returnedByProfileId: string | null;
  status: 'active' | 'returned' | 'revoked' | 'lost' | 'damaged';
  notes: string | null;
}

interface Organization {
  id: string;
  name: string;
}

@Component({
  selector: 'app-drone-detail',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, RemoveDashPipe, DroneResourceUploadModalComponent, ConfirmationModalComponent],
  providers: [RemoveDashPipe],
  templateUrl: './drone-detail.component.html',
  styleUrl: './drone-detail.component.scss'
})
export class DroneDetailComponent implements OnInit {
  drone: DroneInventory | null = null;
  loading = true;
  errorMessage = '';
  droneId: string | null = null;
  archiving = false;
  syncing = false;
  syncSuccessMessage = '';
  private archiveModal: any;

  // Assignment state
  currentAssignment: DroneAssignment | null = null;
  assignmentHistory: DroneAssignment[] = [];
  organizations: Organization[] = [];
  loadingAssignment = false;
  loadingOrganizations = false;
  assigning = false;
  returning = false;
  revoking = false;

  // Assignment form
  selectedOrganizationId = '';
  assignmentNotes = '';
  returnNotes = '';
  revokeReason = '';

  // Organization search
  organizationSearchQuery = '';
  filteredOrganizations: Organization[] = [];
  showOrganizationDropdown = false;
  selectedOrganization: Organization | null = null;

  // Modals
  private assignModal: any;
  private returnModal: any;
  private revokeModal: any;
  private historyModal: any;

  // Reports section
  reportsExpanded = false;
  loadingReports = false;
  reportErrorMessage = '';
  reportStartDate = '';
  reportEndDate = '';
  acreageData: AcreageReportOrgDetail | null = null;
  chemicalData: ChemicalReportOrgDetail | null = null;
  exportingAcreage = false;
  exportingChemical = false;

  // Resources section
  resourcesExpanded = false;
  loadingResources = false;
  droneResources: DroneResource[] = [];
  droneResourcesErrorMessage = '';
  droneResourcesSuccessMessage = '';
  readonly resourceTypes = ['all'].concat(DRONE_RESOURCE_TYPES);
  showResourceUploadModal = false;
  editingResource: DroneResource | null = null;
  resourceType: string = 'all'
  adminOnly: string = 'all';
  showConfirmDeleteModal: boolean = false;
  deletingResource: boolean = false
  resourceToDelete: DroneResource | null = null;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private http: HttpClient,
    private acreageReportService: AcreageReportService,
    private chemicalReportService: ChemicalReportService,
    private droneResourcesService: DroneResourceService,
    private fileUploadService: FileUploadService,
    private removeDash: RemoveDashPipe
  ) {}

  ngOnInit(): void {
    this.droneId = this.route.snapshot.paramMap.get('id');
    if (this.droneId) {
      this.loadDroneDetail();
      this.loadCurrentAssignment();
      this.loadOrganizations();
      // Resources are NOT fetched here — the section starts collapsed and its
      // header shows no count, so toggleResourcesSection owns the first load.
      this.initializeReportDateRange();
    } else {
      this.errorMessage = 'No drone ID provided';
      this.loading = false;
    }
  }

  async loadDroneDetail(): Promise<void> {
    this.loading = true;
    this.errorMessage = '';

    try {
      const response = await firstValueFrom(
        this.http.get<{ success: boolean; data: DroneInventory; error?: string }>(
          `${environment.apiUrl}/admin/drones/${this.droneId}`
        )
      );

      if (response.success && response.data) {
        this.drone = response.data;
      } else {
        this.errorMessage = response.error || 'Failed to load drone details';
      }
    } catch (error: any) {
      console.error('Failed to load drone:', error);
      if (error.status === 404) {
        this.errorMessage = 'Drone not found';
      } else {
        this.errorMessage = 'Failed to load drone details';
      }
    } finally {
      this.loading = false;
    }
  }

  goBack(): void {
    this.router.navigate(['/drones']);
  }

  editDrone(): void {
    this.router.navigate(['/drones', this.droneId, 'edit']);
  }

  confirmArchive(): void {
    const modalElement = document.getElementById('archiveModal');
    if (modalElement) {
      this.archiveModal = new bootstrap.Modal(modalElement);
      this.archiveModal.show();
    }
  }

  async archiveDrone(): Promise<void> {
    if (!this.droneId) return;

    this.archiving = true;

    try {
      await firstValueFrom(
        this.http.delete(`${environment.apiUrl}/admin/drones/${this.droneId}`)
      );

      if (this.archiveModal) {
        this.archiveModal.hide();
      }
      this.router.navigate(['/drones']);
    } catch (error: any) {
      console.error('Failed to archive drone:', error);
      this.errorMessage = 'Failed to archive drone. Please try again.';
      if (this.archiveModal) {
        this.archiveModal.hide();
      }
    } finally {
      this.archiving = false;
    }
  }

  async updateStatus(newStatus: string): Promise<void> {
    if (!this.droneId) return;

    try {
      const response = await firstValueFrom(
        this.http.put<{ success: boolean; data: DroneInventory; error?: string }>(
          `${environment.apiUrl}/admin/drones/${this.droneId}/status`,
          { status: newStatus }
        )
      );

      if (response.success && response.data) {
        this.drone = response.data;
      } else {
        this.errorMessage = response.error || 'Failed to update status';
      }
    } catch (error: any) {
      console.error('Failed to update status:', error);
      this.errorMessage = 'Failed to update drone status';
    }
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'available': return 'bg-success';
      case 'assigned': return 'bg-primary';
      case 'maintenance': return 'bg-warning';
      case 'retired': return 'bg-secondary';
      default: return 'bg-secondary';
    }
  }

  getStatusLabel(status: string): string {
    switch (status) {
      case 'available': return 'Available';
      case 'assigned': return 'Assigned';
      case 'maintenance': return 'Maintenance';
      case 'retired': return 'Retired';
      default: return status;
    }
  }

  formatDate(dateString: string | null): string {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  formatDateTime(dateString: string | null): string {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  getZohoSyncStatusClass(): string {
    switch (this.drone?.zohoFsmSyncStatus) {
      case 'synced': return 'bg-success';
      case 'pending': return 'bg-warning';
      case 'error': return 'bg-danger';
      default: return 'bg-secondary';
    }
  }

  getZohoSyncStatusLabel(): string {
    switch (this.drone?.zohoFsmSyncStatus) {
      case 'synced': return 'Synced';
      case 'pending': return 'Pending';
      case 'error': return 'Error';
      default: return 'Not Synced';
    }
  }

  async syncToZoho(): Promise<void> {
    if (!this.droneId || this.syncing) return;

    this.syncing = true;
    this.errorMessage = '';
    this.syncSuccessMessage = '';

    try {
      const response = await firstValueFrom(
        this.http.post<{ success: boolean; zohoFsmProductId?: string; operation?: string; error?: string }>(
          `${environment.apiUrl}/admin/zoho/sync/drones/${this.droneId}`,
          {}
        )
      );

      if (response.success) {
        this.syncSuccessMessage = response.operation === 'create'
          ? 'Drone created in Zoho FSM'
          : 'Drone updated in Zoho FSM';
        // Reload drone to get updated sync status
        await this.loadDroneDetail();
      } else {
        this.errorMessage = response.error || 'Failed to sync drone to Zoho';
      }
    } catch (error: any) {
      console.error('Failed to sync to Zoho:', error);
      this.errorMessage = error.error?.message || 'Failed to sync drone to Zoho';
    } finally {
      this.syncing = false;
    }
  }

  // ============================================
  // Assignment Methods
  // ============================================

  async loadCurrentAssignment(): Promise<void> {
    if (!this.droneId) return;

    this.loadingAssignment = true;
    try {
      const response = await firstValueFrom(
        this.http.get<{ success: boolean; data: DroneAssignment | null; hasActiveAssignment: boolean }>(
          `${environment.apiUrl}/admin/drones/${this.droneId}/current-assignment`
        )
      );

      if (response.success) {
        this.currentAssignment = response.data;

        // If reports section was expanded before assignment loaded, trigger report load now
        if (this.reportsExpanded && !this.acreageData && !this.chemicalData) {
          this.loadReportData();
        }
      }
    } catch (error: any) {
      console.error('Failed to load current assignment:', error);
    } finally {
      this.loadingAssignment = false;
    }
  }

  async loadOrganizations(): Promise<void> {
    this.loadingOrganizations = true;
    try {
      const response = await firstValueFrom(
        this.http.get<{ success: boolean; data: { organizations: Organization[]; total: number } }>(
          `${environment.apiUrl}/admin/organizations?pageSize=1000&sortBy=name&sortOrder=asc`
        )
      );

      if (response.success && response.data) {
        this.organizations = response.data.organizations;
        this.filteredOrganizations = [...this.organizations];
      }
    } catch (error: any) {
      console.error('Failed to load organizations:', error);
    } finally {
      this.loadingOrganizations = false;
    }
  }

  async loadAssignmentHistory(): Promise<void> {
    if (!this.droneId) return;

    try {
      const response = await firstValueFrom(
        this.http.get<{ success: boolean; data: DroneAssignment[] }>(
          `${environment.apiUrl}/admin/drones/${this.droneId}/assignments`
        )
      );

      if (response.success && response.data) {
        this.assignmentHistory = response.data;
      }
    } catch (error: any) {
      console.error('Failed to load assignment history:', error);
    }
  }

  // Modal controls
  openAssignModal(): void {
    this.selectedOrganizationId = '';
    this.selectedOrganization = null;
    this.organizationSearchQuery = '';
    this.filteredOrganizations = [...this.organizations];
    this.showOrganizationDropdown = false;
    this.assignmentNotes = '';
    const modalElement = document.getElementById('assignModal');
    if (modalElement) {
      this.assignModal = new bootstrap.Modal(modalElement);
      this.assignModal.show();
    }
  }

  // Organization search methods
  onOrganizationSearchInput(): void {
    const query = this.organizationSearchQuery.toLowerCase().trim();
    if (!query) {
      this.filteredOrganizations = [...this.organizations];
    } else {
      this.filteredOrganizations = this.organizations.filter(org =>
        org.name.toLowerCase().includes(query)
      );
    }
    this.showOrganizationDropdown = true;
  }

  onOrganizationSearchFocus(): void {
    this.showOrganizationDropdown = true;
  }

  onOrganizationSearchBlur(): void {
    // Delay to allow click on dropdown item
    setTimeout(() => {
      this.showOrganizationDropdown = false;
    }, 200);
  }

  selectOrganization(org: Organization): void {
    this.selectedOrganization = org;
    this.selectedOrganizationId = org.id;
    this.organizationSearchQuery = org.name;
    this.showOrganizationDropdown = false;
  }

  clearOrganizationSelection(): void {
    this.selectedOrganization = null;
    this.selectedOrganizationId = '';
    this.organizationSearchQuery = '';
    this.filteredOrganizations = [...this.organizations];
  }

  openReturnModal(): void {
    this.returnNotes = '';
    const modalElement = document.getElementById('returnModal');
    if (modalElement) {
      this.returnModal = new bootstrap.Modal(modalElement);
      this.returnModal.show();
    }
  }

  openRevokeModal(): void {
    this.revokeReason = '';
    const modalElement = document.getElementById('revokeModal');
    if (modalElement) {
      this.revokeModal = new bootstrap.Modal(modalElement);
      this.revokeModal.show();
    }
  }

  async openHistoryModal(): Promise<void> {
    await this.loadAssignmentHistory();
    const modalElement = document.getElementById('historyModal');
    if (modalElement) {
      this.historyModal = new bootstrap.Modal(modalElement);
      this.historyModal.show();
    }
  }

  // Assignment actions
  async assignDrone(): Promise<void> {
    if (!this.droneId || !this.selectedOrganizationId || this.assigning) return;

    this.assigning = true;
    this.errorMessage = '';

    try {
      const response = await firstValueFrom(
        this.http.post<{ success: boolean; data: DroneAssignment; error?: string }>(
          `${environment.apiUrl}/admin/drones/${this.droneId}/assign`,
          {
            organizationId: this.selectedOrganizationId,
            notes: this.assignmentNotes || undefined,
          }
        )
      );

      if (response.success) {
        this.currentAssignment = response.data;
        if (this.assignModal) {
          this.assignModal.hide();
        }
        // Reload drone to get updated status
        await this.loadDroneDetail();
      } else {
        this.errorMessage = response.error || 'Failed to assign drone';
      }
    } catch (error: any) {
      console.error('Failed to assign drone:', error);
      this.errorMessage = error.error?.error || 'Failed to assign drone';
    } finally {
      this.assigning = false;
    }
  }

  async returnDrone(): Promise<void> {
    if (!this.droneId || this.returning) return;

    this.returning = true;
    this.errorMessage = '';

    try {
      const response = await firstValueFrom(
        this.http.post<{ success: boolean; data: DroneAssignment; error?: string }>(
          `${environment.apiUrl}/admin/drones/${this.droneId}/return`,
          {
            notes: this.returnNotes || undefined,
          }
        )
      );

      if (response.success) {
        this.currentAssignment = null;
        if (this.returnModal) {
          this.returnModal.hide();
        }
        // Reload drone to get updated status
        await this.loadDroneDetail();
      } else {
        this.errorMessage = response.error || 'Failed to return drone';
      }
    } catch (error: any) {
      console.error('Failed to return drone:', error);
      this.errorMessage = error.error?.error || 'Failed to return drone';
    } finally {
      this.returning = false;
    }
  }

  async revokeDrone(): Promise<void> {
    if (!this.droneId || !this.revokeReason.trim() || this.revoking) return;

    this.revoking = true;
    this.errorMessage = '';

    try {
      const response = await firstValueFrom(
        this.http.post<{ success: boolean; data: DroneAssignment; error?: string }>(
          `${environment.apiUrl}/admin/drones/${this.droneId}/revoke`,
          {
            reason: this.revokeReason.trim(),
          }
        )
      );

      if (response.success) {
        this.currentAssignment = null;
        if (this.revokeModal) {
          this.revokeModal.hide();
        }
        // Reload drone to get updated status
        await this.loadDroneDetail();
      } else {
        this.errorMessage = response.error || 'Failed to revoke assignment';
      }
    } catch (error: any) {
      console.error('Failed to revoke assignment:', error);
      this.errorMessage = error.error?.error || 'Failed to revoke assignment';
    } finally {
      this.revoking = false;
    }
  }

  getAssignmentStatusClass(status: string): string {
    switch (status) {
      case 'active': return 'bg-success';
      case 'returned': return 'bg-secondary';
      case 'revoked': return 'bg-danger';
      case 'lost': return 'bg-dark';
      case 'damaged': return 'bg-warning';
      default: return 'bg-secondary';
    }
  }

  getAssignmentStatusLabel(status: string): string {
    switch (status) {
      case 'active': return 'Active';
      case 'returned': return 'Returned';
      case 'revoked': return 'Revoked';
      case 'lost': return 'Lost';
      case 'damaged': return 'Damaged';
      default: return status;
    }
  }

  getOrganizationName(organizationId: string): string {
    const org = this.organizations.find(o => o.id === organizationId);
    return org?.name || 'Unknown Organization';
  }

  // ============================================
  // Reports Section Methods
  // ============================================

  /**
   * Initialize date range for reports to current month (first and last day)
   */
  private initializeReportDateRange(): void {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    this.reportStartDate = this.formatDateForInput(firstDay);
    this.reportEndDate = this.formatDateForInput(lastDay);
  }

  /**
   * Format date for HTML date input (YYYY-MM-DD)
   */
  formatDateForInput(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Toggle reports section expansion
   */
  toggleReportsSection(): void {
    this.reportsExpanded = !this.reportsExpanded;

    // Load reports data when expanding for the first time
    if (this.reportsExpanded && !this.acreageData && !this.chemicalData) {
      this.loadReportData();
    }
  }

  toggleResourcesSection(): void {
    this.resourcesExpanded = !this.resourcesExpanded;

    if (this.resourcesExpanded) {
      this.loadDroneResources();
    }
  }

  /**
   * Handle report filter change
   */
  onReportFilterChange(): void {
    if (this.reportsExpanded) {
      this.loadReportData();
    }
  }

  /**
   * Load both acreage and chemical report data scoped to this drone
   */
  async loadReportData(): Promise<void> {
    if (!this.droneId || !this.reportStartDate || !this.reportEndDate || !this.currentAssignment) {
      // Reports require an active assignment to have an organization context
      return;
    }

    this.loadingReports = true;
    this.reportErrorMessage = '';

    try {
      // Load both reports in parallel, scoped to this drone
      const [acreage, chemical] = await Promise.all([
        this.acreageReportService.getOrganizationDetail(
          this.currentAssignment.organizationId,
          this.reportStartDate,
          this.reportEndDate,
          this.droneId
        ),
        this.chemicalReportService.getOrganizationDetail(
          this.currentAssignment.organizationId,
          this.reportStartDate,
          this.reportEndDate,
          undefined,
          this.droneId
        )
      ]);

      this.acreageData = acreage;
      this.chemicalData = chemical;
    } catch (error: any) {
      console.error('Failed to load report data:', error);
      this.reportErrorMessage = 'Failed to load report data. Please try again.';
    } finally {
      this.loadingReports = false;
    }
  }

  async loadDroneResources(): Promise<void> {
    if (!this.droneId) {
      return;
    }

    this.loadingResources = true;
    this.droneResourcesErrorMessage = '';

    const resourceTypeClean = this.resourceType === 'all' ? '' : this.resourceType;
    const adminOnlyClean = this.adminOnly === 'all' ? '' : this.adminOnly;

    try {
      const resources = await this.droneResourcesService.getResourcesByDroneId(
        this.droneId,
        resourceTypeClean,
        adminOnlyClean,
      )
      this.droneResources = resources ?? [];
    } catch (error: any) {
      console.error('Failed to load any drone resources: ', error);
      this.droneResourcesErrorMessage = 'Failed to load drone resources. Please try again';
    } finally {
      this.loadingResources = false;
    }
  }

  onResourceAdded(resource: DroneResource): void {
    // Reset the filters so the file they just added is guaranteed visible,
    // then refetch — the server decides what matches, not a local prepend.
    this.resourceType = 'all';
    this.adminOnly = 'all';
    this.closeResourceUploadModal();
    this.loadDroneResources();
    this.droneResourcesSuccessMessage = `Attached ${this.removeDash.transform(resource.resourceType ?? 'other')} document "${resource.fileName}" to ${this.drone?.name}`;

  }

  onResourceUpdated(resource: DroneResource): void {
    // Filters stay put here: an edit that no longer matches SHOULD drop out.
    this.closeResourceUploadModal();
    this.loadDroneResources();
    this.droneResourcesSuccessMessage = `Edited ${this.removeDash.transform(resource.resourceType ?? 'other')} document "${resource.fileName}" to ${this.drone?.name}`;
  }

  async onResourceDeleteConfirm(): Promise<void> {
    if (!this.drone || !this.resourceToDelete) {
      console.error('Drone or resourceToDelete is null');
      return;
    }

    // Captured before the reset below — the success copy needs it.
    const fileName = this.resourceToDelete.fileName;
    this.deletingResource = true;

    try {
      await this.droneResourcesService.deleteResource(this.drone.id, this.resourceToDelete.id);
      this.droneResourcesSuccessMessage = `Removed "${fileName}" from ${this.drone.name}`;
      this.resourceToDelete = null;
    } catch (error: any) {
      console.error('Unable to delete resource', error);
      this.droneResourcesErrorMessage = error?.message || 'Could not delete this resource.';
    } finally {
      this.deletingResource = false;
      this.showConfirmDeleteModal = false;
      this.loadDroneResources();
    }
  }

  /**
   * Export acreage report to PDF
   */
  async exportAcreagePdf(): Promise<void> {
    if (!this.acreageData || this.exportingAcreage || !this.currentAssignment) return;

    this.exportingAcreage = true;
    this.reportErrorMessage = '';

    try {
      const filters = {
        startDate: this.reportStartDate,
        endDate: this.reportEndDate,
        organizationId: this.currentAssignment.organizationId,
        droneId: this.droneId || undefined
      };

      const result = await this.acreageReportService.exportPdf(
        'organization-detail',
        filters,
        this.acreageData
      );

      if (result && result.downloadUrl) {
        window.open(result.downloadUrl, '_blank');
      }
    } catch (error: any) {
      console.error('Failed to export acreage PDF:', error);
      this.reportErrorMessage = 'Failed to export acreage PDF';
    } finally {
      this.exportingAcreage = false;
    }
  }

  /**
   * Export chemical report to PDF
   */
  async exportChemicalPdf(): Promise<void> {
    if (!this.chemicalData || this.exportingChemical || !this.currentAssignment) return;

    this.exportingChemical = true;
    this.reportErrorMessage = '';

    try {
      const filters = {
        startDate: this.reportStartDate,
        endDate: this.reportEndDate,
        organizationId: this.currentAssignment.organizationId,
        droneId: this.droneId || undefined
      };

      const result = await this.chemicalReportService.exportPdf(
        'organization-detail',
        filters,
        this.chemicalData
      );

      if (result && result.downloadUrl) {
        window.open(result.downloadUrl, '_blank');
      }
    } catch (error: any) {
      console.error('Failed to export chemical PDF:', error);
      this.reportErrorMessage = 'Failed to export chemical PDF';
    } finally {
      this.exportingChemical = false;
    }
  }

  /**
   * Format number with commas
   */
  formatNumber(num: number | null | undefined): string {
    if (num === null || num === undefined) return '0';
    return num.toLocaleString('en-US', { maximumFractionDigits: 2 });
  }

  openResourceUploadModal(resource?: DroneResource): void {
    // Null on the create path, otherwise the next "Attach Resource" reopens
    // in edit mode against the last-edited row.
    this.editingResource = resource ?? null;
    this.showResourceUploadModal = true;
  }

  openConfirmDeleteModal(resource: DroneResource): void {
    this.resourceToDelete = resource;
    this.showConfirmDeleteModal = true;
  }

  closeResourceUploadModal(): void {
    this.showResourceUploadModal = false;
    this.editingResource = null;
  }

  formatFileSize(bytes: number | null): string {
    if (bytes === null) return '';
    return this.fileUploadService.formatFileSize(bytes);
  }

  async getDroneResourceFile(resource: DroneResource) {
    if (this.drone?.id) {
      // Opened before the await so it stays inside the user-gesture window —
      // a popup blocker would eat a window.open that happens after it.
      const tab = window.open('', '_blank');
      // Sever the back-reference so the opened page can't navigate this one.
      if (tab) tab.opener = null;

      try {
        const signedUrl = await this.droneResourcesService.getResourceDownloadUrl(this.drone.id, resource.id);
        if (tab) {
          tab.location.href = signedUrl;
        } else {
          window.location.href = signedUrl;
        }
      } catch(error) {
        tab?.close();
        this.errorMessage = 'Could not get resource file'
      }
    } else {
      console.error('Drone ID is undefined');
    }
  }
}

